import React, { useState, useReducer, useRef } from 'react';
// On importe notre arbre de données initial
import { initialTree } from './decisionTree';
import Game from './components/Game';
import Guess from './components/Guess';
import AddNewProcedure from './components/AddNewProcedure';
import './App.css';

// La fonction reducer a besoin de l'arbre de données pour fonctionner,
// donc nous allons le passer en argument de l'action.
function gameReducer(state, action) {
  switch (action.type) {
    case 'ANSWER': {
      const { treeData, answer } = action.payload;
      const currentNode = treeData[state.currentNodeId];
      const nextNodeId = currentNode.answers[answer];
      const nextNode = treeData[nextNodeId];

      return {
        ...state,
        history: [...state.history, state.currentNodeId],
        currentNodeId: nextNodeId,
        gameState: nextNode.type === 'question' ? 'playing' : 'guessing',
      };
    }
    case 'REJECT_GUESS': {
      const { treeData } = action.payload;
      return {
        ...state,
        gameState: 'addNew',
        wrongGuess: treeData[state.currentNodeId],
      };
    }
    case 'RESTART': {
      const { rootNodeId } = action.payload;
      return {
        gameState: 'playing',
        currentNodeId: rootNodeId,
        history: [],
        wrongGuess: null,
      };
    }
    default:
      throw new Error();
  }
}

function App() {
  // L'arbre de décision est maintenant dans un état
  const [tree, setTree] = useState(initialTree);
  const fileInputRef = useRef(null);

  const [state, dispatch] = useReducer(gameReducer, {
    gameState: 'playing',
    currentNodeId: tree.rootNodeId,
    history: [],
    wrongGuess: null,
  });

  const currentNode = tree.data[state.currentNodeId];

  const handleRestart = () => {
    dispatch({ type: 'RESTART', payload: { rootNodeId: tree.rootNodeId } });
  };

  // Logique pour l'exportation
  const handleExport = () => {
    const jsonString = JSON.stringify(tree, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'guide_administratif_data.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Logique pour l'importation
  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        // Validation simple pour s'assurer que le fichier a la bonne structure
        if (importedData.rootNodeId && importedData.data) {
          setTree(importedData);
          dispatch({ type: 'RESTART', payload: { rootNodeId: importedData.rootNodeId } });
        } else {
          alert('Le fichier JSON importé est invalide ou mal formaté.');
        }
      } catch (error) {
        alert('Erreur lors de la lecture du fichier JSON.');
        console.error("JSON Parse Error:", error);
      }
    };
    reader.readAsText(file);
    // Réinitialise la valeur pour permettre de réimporter le même fichier
    event.target.value = null; 
  };
  
  const triggerFileSelect = () => {
    fileInputRef.current.click();
  };

  const renderGameState = () => {
    switch (state.gameState) {
      case 'playing':
        return (
          <Game
            node={currentNode}
            onAnswer={(answer) =>
              dispatch({ type: 'ANSWER', payload: { answer, treeData: tree.data } })
            }
          />
        );
      case 'guessing':
        return (
          <Guess
            procedure={currentNode}
            onConfirm={handleRestart}
            onReject={() => dispatch({ type: 'REJECT_GUESS', payload: { treeData: tree.data } })}
          />
        );
      case 'addNew':
        return <AddNewProcedure wrongGuess={state.wrongGuess} onFinish={handleRestart} />;
      default:
        return <button onClick={handleRestart}>Commencer</button>;
    }
  };

  const questionCount = state.history.length + 1;

  return (
    <div className="container">
      <header>
        <h1>Le Guide Administratif</h1>
        <p>Je devinerai la démarche administrative dont vous avez besoin en moins de 20 questions.</p>
      </header>
      <main className="game-box">
        {state.gameState !== 'addNew' && (
          <div className="progress-bar">
            <div className="progress" style={{ width: `${(questionCount / 20) * 100}%` }}></div>
            <span>Question {questionCount} sur 20</span>
          </div>
        )}
        {renderGameState()}
      </main>
      <footer>
        {state.history.length > 0 && <button onClick={handleRestart} className="btn-secondary">Recommencer</button>}
        <div className="io-buttons">
          <input
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={handleImport}
          />
          <button onClick={triggerFileSelect}>Importer les données</button>
          <button onClick={handleExport}>Exporter les données</button>
        </div>
      </footer>
    </div>
  );
}

export default App;